// Regions of the World Map. When one opens it gets a signpost on Base Camp, a guide, and a daily
// quest: a round of one of the region's games with a bigger bonus than a Town job.

import { seedFrom } from '@kit/rng';
import { GAMES, lockInfo } from './registry';
import { dayKey, getSave, totalStars, type Save } from './state';
import type { GameModule } from './types';
import type { Neighbor, TodayJob } from './town';

export interface RegionDef {
  id: string;
  name: string;
  stars: number;
  color: string;
  guide: { name: string; role: string };
  charm: string;
  filter: (g: GameModule) => boolean;
  /** quest story, with {game} replaced by the game title */
  stories: string[];
  thanks: string;
}

export const REGION_DEFS: RegionDef[] = [
  {
    id: 'woods', name: 'Word Woods', stars: 12, color: '#9b6be6', charm: 'charm.region.woods',
    guide: { name: 'Old Owl', role: 'keeper of the Word Woods' },
    filter: g => g.category === 'words',
    stories: ['Old Owl has lost words all over the purple woods. Bring them back with {game}.', 'The trees of the Word Woods whisper riddles. Old Owl says {game} will quiet them.', 'Old Owl is writing a new book and needs a helper. Today that means {game}.'],
    thanks: 'Old Owl hoots three times. That means thank you.',
  },
  {
    id: 'mines', name: 'Number Mines', stars: 12, color: '#4f8fe6', charm: 'charm.region.mines',
    guide: { name: 'Digby Mole', role: 'chief of the Number Mines' },
    filter: g => g.category === 'math',
    stories: ['Digby Mole found a seam of number gems. Dig them out with {game}.', 'The mine cart is stuck on a tricky track. Digby says {game} will get it rolling.', 'Digby Mole counts everything twice. Help him check today with {game}.'],
    thanks: 'Digby Mole polishes a gem and hands it over with a grin.',
  },
  {
    id: 'volcano', name: 'Volcano Isle', stars: 20, color: '#e2574c', charm: 'charm.region.volcano',
    guide: { name: 'Captain Ember', role: 'ranger of Volcano Isle' },
    filter: g => g.category === 'science' && g.skills.some(s => s === 'geology' || s === 'paleontology' || s === 'earth_science'),
    stories: ['Captain Ember spotted strange rocks after the last rumble. Investigate with {game}.', 'The island is giving up its secrets. Captain Ember needs a scientist for {game}.', 'A storm is brewing over the volcano. Captain Ember asks for help with {game}.'],
    thanks: 'Captain Ember gives you a warm salute from the crater rim.',
  },
  {
    id: 'harbor', name: 'Star Harbor', stars: 30, color: '#3bb3a9', charm: 'charm.region.harbor',
    guide: { name: 'Keeper Lumen', role: 'lighthouse keeper of Star Harbor' },
    filter: g => g.category === 'science' && g.skills.some(s => s === 'space' || s === 'physics' || s === 'chemistry' || s === 'biology'),
    stories: ['Keeper Lumen needs the lighthouse ready for tonight. Help with {game}.', 'Ships are asking the harbor questions. Keeper Lumen says {game} has the answers.', 'The stars look different tonight. Keeper Lumen wants to understand why with {game}.'],
    thanks: 'Keeper Lumen flashes the big light just for you.',
  },
];

export const REGION_BY_ID: Record<string, RegionDef> = Object.fromEntries(REGION_DEFS.map(r => [r.id, r]));

export const REGION_BONUS = 35;

export function regionOpen(r: RegionDef, s: Save = getSave()): boolean {
  return totalStars(s) >= r.stars;
}

export function openRegions(s: Save = getSave()): RegionDef[] {
  return REGION_DEFS.filter(r => regionOpen(r, s));
}

/** the guide as a Town neighbor, so a region quest runs through the same job flow */
function guideNeighbor(r: RegionDef): Neighbor {
  return { id: `region.${r.id}`, name: r.guide.name, role: r.guide.role, color: '#f6efdc', roof: r.color, unlockStars: r.stars, gifts: [], jobs: [] };
}

/** today's quest for each open region: an unlocked game from that region, chosen by date */
export function regionQuestsForToday(s: Save = getSave(), day = dayKey()): TodayJob[] {
  const done = s.town.done[day] || [];
  const out: TodayJob[] = [];
  for (const r of REGION_DEFS) {
    if (!regionOpen(r, s)) continue;
    const games = GAMES.filter(g => r.filter(g) && lockInfo(g, s).unlocked);
    if (!games.length) continue;
    const seed = seedFrom(day, r.id);
    const g = games[seed % games.length];
    const story = r.stories[seed % r.stories.length].replace('{game}', g.title);
    const key = `region.${r.id}:${day}`;
    out.push({
      neighbor: guideNeighbor(r),
      job: { id: `region.${r.id}.${g.id}`, gameId: g.id, title: `${r.name} quest`, story, thanks: r.thanks, bonus: REGION_BONUS },
      key, done: done.includes(key), locked: false,
    });
  }
  return out;
}

export function regionIdFromKey(key: string): string | null {
  const m = /^region\.([a-z]+):/.exec(key);
  return m ? m[1] : null;
}
